import { motion } from "framer-motion";
import { ExternalLink, Heart } from "lucide-react";
import { getPlatformMeta } from "@/lib/socialPlatforms";
import DiscordWidget from "@/components/DiscordWidget";
import { useLang } from "@/i18n/LangContext";

export default function SocialMedia({ links = [] }) {
    const { t, lang } = useLang();

    const social = links.filter((l) => l.category === "social" || (!l.category));
    const contact = links.filter((l) => l.category === "kontakt");
    const support = links.filter((l) => l.category === "support");

    // We still render the section if we have links OR the Discord widget can show up.
    if (!links.length) {
        // Render only Discord widget (it will silently no-op if not configured).
        return (
            <section
                id="kontakt"
                data-testid="social-section"
                className="relative py-24 md:py-32"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <DiscordWidget />
                </div>
            </section>
        );
    }

    return (
        <section
            id="kontakt"
            data-testid="social-section"
            className="relative py-24 md:py-32"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
                    <div className="lg:col-span-6">
                        <div className="label-eyebrow mb-4">{t("09 - Społeczność")}</div>
                        <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl tracking-tighter text-white">
                            {lang === "en" ? (
                                t("Dołącz i wspieraj")
                            ) : (
                                <>Dołącz<br />i wspieraj</>
                            )}
                        </h2>
                    </div>
                    <p className="lg:col-span-6 text-base text-[color:var(--text-secondary)] max-w-xl">
                        {t("Śledź rozwój projektu w social mediach, napisz wiadomość lub - jeśli chcesz - wesprzyj dalszą pracę nad mapą.")}
                    </p>
                </div>

                <DiscordWidget />

                {social.length > 0 && (
                    <div className="mb-12">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[color:var(--text-secondary)] mb-5">
                            {t("Social media")}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                            {social.map((link, i) => (
                                <SocialCard key={link.id} link={link} index={i} />
                            ))}
                        </div>
                    </div>
                )}

                {contact.length > 0 && (
                    <div className="mb-12">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[color:var(--text-secondary)] mb-5">
                            {t("Kontakt")}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {contact.map((link, i) => (
                                <SocialCard key={link.id} link={link} index={i} />
                            ))}
                        </div>
                    </div>
                )}

                {support.length > 0 && (
                    <div>
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-[color:var(--primary)] mb-5">
                            <Heart size={12} /> {t("Wspieraj projekt")}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {support.map((link, i) => (
                                <SupportCard key={link.id} link={link} index={i} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

function SocialCard({ link, index }) {
    const { t } = useLang();
    const meta = getPlatformMeta(link.platform);
    const Icon = meta.Icon;
    const href = link.url.startsWith("mailto:") || /^https?:\/\//.test(link.url)
        ? link.url
        : link.platform === "email"
        ? `mailto:${link.url}`
        : `https://${link.url}`;

    return (
        <motion.a
            data-testid={`social-card-${link.id}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ delay: (index % 8) * 0.04, duration: 0.4 }}
            className="group relative flex items-center gap-4 p-5 rounded-xl border border-white/5 bg-[color:var(--bg-surface)] transition-all duration-300 hover:-translate-y-1"
            style={{
                "--hover-color": meta.color,
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = `${meta.color}55`;
                e.currentTarget.style.boxShadow = `0 8px 30px ${meta.color}1a`;
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.05)";
                e.currentTarget.style.boxShadow = "none";
            }}
        >
            <div
                className="w-11 h-11 rounded-md flex items-center justify-center shrink-0 transition-colors"
                style={{ background: `${meta.color}1a`, color: meta.color }}
            >
                <Icon size={20} strokeWidth={1.7} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)]">
                    {t(meta.label)}
                </div>
                <div className="text-sm text-white font-medium truncate">
                    {link.label ? t(link.label) : t(meta.label)}
                </div>
            </div>
            <ExternalLink
                size={14}
                className="text-[color:var(--text-muted)] group-hover:text-white shrink-0"
            />
        </motion.a>
    );
}

function SupportCard({ link, index }) {
    const { t } = useLang();
    const meta = getPlatformMeta(link.platform);
    const Icon = meta.Icon;
    const href = /^https?:\/\//.test(link.url) ? link.url : `https://${link.url}`;
    // Support cards always use a green glow ("lighting") regardless of platform
    // so donation/support CTAs feel visually distinct and positive.
    const glow = "#22c55e";

    return (
        <motion.a
            data-testid={`support-card-${link.id}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ delay: index * 0.05, duration: 0.45 }}
            className="group relative p-6 rounded-xl border bg-[color:var(--bg-surface)] overflow-hidden transition-all duration-300 hover:-translate-y-1"
            style={{ borderColor: `${glow}80` }}
            onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = `0 10px 40px ${glow}55`;
                e.currentTarget.style.borderColor = glow;
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.borderColor = `${glow}80`;
            }}
        >
            <div
                className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl pointer-events-none"
                style={{ background: `${glow}33` }}
            />
            <div className="relative flex items-start gap-4">
                <div
                    className="w-12 h-12 rounded-md flex items-center justify-center shrink-0"
                    style={{ background: `${glow}26`, color: glow }}
                >
                    <Icon size={22} strokeWidth={1.7} />
                </div>
                <div className="flex-1">
                    <div
                        className="text-[10px] uppercase tracking-[0.3em] mb-1"
                        style={{ color: glow }}
                    >
                        {t(meta.label)}
                    </div>
                    <div className="font-display uppercase text-lg text-white mb-1 tracking-tight">
                        {link.label ? t(link.label) : t(`Wesprzyj na ${meta.label}`)}
                    </div>
                    <div className="text-xs text-[color:var(--text-muted)] truncate">
                        {link.url}
                    </div>
                </div>
                <ExternalLink size={14} className="text-[color:var(--text-muted)]" />
            </div>
        </motion.a>
    );
}
